import { useCallback, useRef, useState, PropsWithChildren } from "react";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { CommonEditorInput } from "./common-editor-input";
import { MDSComboboxItem, MDSComboboxSlotName } from "./combobox.types";
import {
  StyledCombobox,
  StyledLabel,
  StyledInputContainer,
  StyledLeftContainer,
  StyledRightContainer,
  StyledLeftIconContainer,
  StyledInputArea,
  StyledListBoxItem,
  StyledErrorMessage,
} from "./combobox.style";
import { MDSPopover } from "../../overlays";
import { MDSListBox } from "../../lists";
import {
  MemCommonMentionKind,
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
} from "@mem-labs/common-editor";
import type { PopoverProps, Selection } from "react-aria-components";
import { MDSTextFieldColor } from "../text-field";
import { getSlot } from "@/mds/utils/slots";
import styled from "@emotion/styled";

interface Props<T extends MDSComboboxItem> extends PropsWithChildren {
  leftIcon?: MdsIconKind;
  isLoading: boolean;
  label?: string;
  placeholder?: string;
  errorMessage?: string;
  isDisabled?: boolean;
  color?: MDSTextFieldColor;
  fetchData: ({ query }: { query: string }) => Promise<MDSComboboxDataSource<T>>;
  popoverProps?: PopoverProps;
}

interface MDSComboboxDataSource<T extends MDSComboboxItem> {
  data: T[];
  error?: string;
  isLoading?: boolean;
}

export const MDSCombobox = <T extends MDSComboboxItem>(props: Props<T>) => {
  const {
    fetchData,
    // isLoading,
    leftIcon,
    label,
    placeholder,
    errorMessage,
    isDisabled = false,
    color,
    children,
    popoverProps,
  } = props;

  const [state, setState] = useState<{ isOpen: boolean; query: string }>({
    isOpen: false,
    query: "",
  });

  const [data, setData] = useState<T[]>([]);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<MemCommonRichTextInputInstance>(null);

  // Handle mention queries
  const handleMentionQuery = useCallback(
    async (queryStr: string) => {
      // Remove first symbol if it starts with @ or #
      let query = queryStr.trim();
      query = ["@", "#"].includes(query.charAt(0)) ? query.slice(1) : query;

      if (query.length >= 0) {
        // Fetch data if we have a data source
        setState(state => ({ ...state, isOpen: true }));
        fetchData({ query }).then(({ data: d }) => {
          setData(d);
        });
      } else {
        setState(state => ({ ...state, isOpen: false }));
      }

      setState(state => ({ ...state, query }));
    },

    [fetchData]
  );

  const handleSelection = useCallback((keys: Selection) => {
    const selected = keys as Set<string>;
    const mention = {
      id: "uuid",
      kind: MemCommonMentionKind.Note,
      label: "Note name " + Array.from(selected.values()).join(", "),
      metadata: "",
    };

    inputRef.current?.dispatchAction?.({
      kind: MemCommonRichTextInputActionKind.CloseMention,
      payload: {
        id: mention.id,
        kind: mention.kind ?? MemCommonMentionKind.Note,
        label: mention.label,
        // action,
        metadata: mention.metadata,
      },
    });

    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.FocusEditor,
      payload: null,
    });

    setState({ isOpen: false, query: "" });
  }, []);

  return (
    <StyledCombobox ref={rootRef}>
      <StyledLabel>{label}</StyledLabel>

      <StyledInputContainer color={color} aria-disabled={isDisabled}>
        {/* Left addon - icon or custom slot */}
        <StyledLeftContainer color={color}>
          {leftIcon && (
            <StyledLeftIconContainer>
              <MdsIcon kind={leftIcon} />
            </StyledLeftIconContainer>
          )}
          {getSlot(children, MDSComboboxSlotName.LeftAddon)}
        </StyledLeftContainer>

        {/* Main input area */}
        <StyledInputArea>
          <CommonEditorInput
            onMentionQuerySet={handleMentionQuery}
            placeholder={placeholder}
            isDisabled={isDisabled}
            inputRef={inputRef}
          />
        </StyledInputArea>

        {/* Right addon - action button, dropdown trigger, or custom slot */}
        <StyledRightContainer color={color}>
          {/* Custom action button */}
          {/* {onAction && (
            <StyledActionButtonContainer isActive={isActionButtonActive}>
              <MDSButton
                onPress={onAction}
                size="sm"
                color="transparent"
                isDisabled={!isActionButtonActive}
                {...actionButtonProps}
              >
                {actionButtonProps?.children || (
                  <MdsIcon kind={isActionButtonActive ? MdsIconKind.Send : MdsIconKind.SendAlt} />
                )}
              </MDSButton> */}
          {/* </StyledActionButtonContainer>
          )} */}

          {/* Custom right addon slot */}
          {getSlot(children, MDSComboboxSlotName.RightAddon)}
        </StyledRightContainer>
      </StyledInputContainer>

      <MDSPopover
        isNonModal
        triggerRef={rootRef}
        isOpen={state.isOpen}
        onOpenChange={isOpen => setState(state => ({ ...state, isOpen }))}
        {...popoverProps}
      >
        {data.length > 0 ? (
          <MDSListBox
            onSelectionChange={handleSelection}
            selectionMode="single"
            shouldFocusWrap={true}
          >
            {data.map((item: MDSComboboxItem) => (
              <StyledListBoxItem key={item.id} id={item.id}>
                {item.label}
              </StyledListBoxItem>
            ))}
          </MDSListBox>
        ) : (
          <EmptyResult>Nothing found</EmptyResult>
        )}
      </MDSPopover>

      {errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
    </StyledCombobox>
  );
};

const EmptyResult = styled.div`
  padding: 1rem;
  text-align: center;
`;
